import { combine, createStore, createEvent, sample } from 'effector';

export const createTableModel = (
  $sourceStore,
  defaultSort = 'id',
  itemsPerPage = 10,
) => {
  const sortBy = createEvent();

  const $sortField = createStore(defaultSort).on(sortBy, (_, field) => field);

  const $sortDirection = createStore('asc').on(sortBy, (current, field) =>
    field === $sortField.getState()
      ? current === 'asc'
        ? 'desc'
        : 'asc'
      : 'asc',
  );

  const $sorted = combine(
    $sourceStore,
    $sortField,
    $sortDirection,
    (items, field, direction) => {
      return [...items].sort((a, b) => {
        const aVal = a[field];
        const bVal = b[field];

        if (typeof aVal === 'string') {
          return direction === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        } else if (typeof aVal === 'number') {
          return direction === 'asc' ? aVal - bVal : bVal - aVal;
        }
        return 0;
      });
    },
  );

  const prevPage = createEvent();
  const nextPage = createEvent();

  const $currentPage = createStore(1).on(prevPage, page =>
    Math.max(page - 1, 1),
  );

  const $totalPages = combine($sorted, sorted =>
    Math.max(1, Math.ceil(sorted.length / itemsPerPage)),
  );

  sample({
    clock: nextPage,
    source: combine({ page: $currentPage, total: $totalPages }),
    fn: ({ page, total }) => Math.min(page + 1, total),
    target: $currentPage,
  });

  const $isShownPrevBtn = $currentPage.map(page => page > 1);
  const $isShownNextBtn = combine(
    $currentPage,
    $totalPages,
    (page, total) => page < total,
  );

  const $isShownPagination = combine(
    $isShownPrevBtn,
    $isShownNextBtn,
    (prev, next) => prev || next,
  );

  const $pagedItems = combine($sorted, $currentPage, (items, page) => {
    const start = (page - 1) * itemsPerPage;
    return items.slice(start, start + itemsPerPage);
  });

  return {
    store: {
      $pagedItems,
      $currentPage,
      $totalPages,
      $isShownPrevBtn,
      $isShownNextBtn,
      $isShownPagination,
      $sortField,
      $sortDirection,
    },
    actions: {
      sortBy,
      prevPage,
      nextPage,
    },
  };
};
